import {UserGroup} from '../model';
import type {EventsPermissions} from './permissions/eventsPermissions';
import type {GenericPermissions} from './permissions/genericPermissions';
import type {ResidentsPermissions} from './permissions/residentsPermissions';
import type {RoomsPermissions} from './permissions/roomsPermissions';
import type {SettingsPermissions} from './permissions/settingsPermissions';
import type {TasksPermissions} from './permissions/tasksPermissions';
import type {UsersPermissions} from './permissions/usersPermissions';
import type {InventoryPermissions} from './permissions/inventoryPermissions';
import type {MedicationPermissions} from './permissions/medicationPermissions';
import type {HealthLogPermissions} from './permissions/healthLogPermissions';
import type {RelativesPermissions} from './permissions/relativesPermissions';
import type {WorkTimePermissions} from './permissions/workTimePermissions';
import type {WorkSchedulePermissions} from './permissions/workSchedulePermissions';
import type {PairedDevicesPermissions} from './permissions/pairedDevicesPermissions';
import type {RehabilitationPermissions} from './permissions/rehabilitationPermissions';

export type Permissions =
  | EventsPermissions
  | ResidentsPermissions
  | RoomsPermissions
  | SettingsPermissions
  | TasksPermissions
  | UsersPermissions
  | InventoryPermissions
  | MedicationPermissions
  | HealthLogPermissions
  | RelativesPermissions
  | WorkTimePermissions
  | WorkSchedulePermissions
  | PairedDevicesPermissions
  | RehabilitationPermissions
  | GenericPermissions;

export type RolePermissions = Record<UserGroup, Permissions[]>;

export const rolePermissions: RolePermissions = {
  [UserGroup.Superusers]: ['FULL_ACCESS'],

  [UserGroup.Owners]: ['FULL_ACCESS'],

  [UserGroup.Admins]: ['FULL_ACCESS'],

  [UserGroup.Moderators]: [
    // Events
    'VIEW_EVENTS',
    'VIEW_LATEST_EVENTS',
    'CREATE_EVENT',
    'UPDATE_EVENT',
    // Residents
    'VIEW_RESIDENTS',
    'VIEW_RESIDENT',
    'UPDATE_RESIDENT_CONDITIONS',
    // Rooms
    'VIEW_ROOMS',
    // Tasks
    'VIEW_TASKS',
    'VIEW_TODAY_TASKS',
    'CREATE_TASK',
    'UPDATE_TASK',
    // Medication
    'CREATE_MEDICATION',
    'UPDATE_MEDICATION',
    'DISTRIBUTE_MEDICATION',
    // Health Log
    'CREATE_HEALTH_LOG_MEASUREMENT',
    // Inventory
    'VIEW_WAREHOUSES',
    'VIEW_WAREHOUSE',
    'CREATE_WAREHOUSE_ITEM',
    'UPDATE_WAREHOUSE_ITEM',
    'DELETE_WAREHOUSE_ITEM',
    // Work schedule
    'VIEW_WORK_SCHEDULE',
    // Rehabilitation
    'MANAGE_REHABILITATION',
    // Generic
    'VIEW_MENU',
  ],

  [UserGroup.EmployeesInventory]: [
    // Events
    'VIEW_LATEST_EVENTS',
    'CREATE_EVENT',
    // Tasks
    'VIEW_TODAY_TASKS',
    // Inventory
    'VIEW_WAREHOUSES',
    'VIEW_WAREHOUSE',
    'CREATE_WAREHOUSE_ITEM',
    'UPDATE_WAREHOUSE_ITEM',
    'DELETE_WAREHOUSE_ITEM',
    // Work schedule
    'VIEW_WORK_SCHEDULE',
    // Generic
    'VIEW_MENU',
  ],

  [UserGroup.EmployeesRehabilitation]: [
    // Health Log
    'CREATE_HEALTH_LOG_MEASUREMENT',
    // Events
    'VIEW_LATEST_EVENTS',
    'CREATE_EVENT',
    // Tasks
    'VIEW_TODAY_TASKS',
    // Work schedule
    'VIEW_WORK_SCHEDULE',
    // Rehabilitation
    'MANAGE_REHABILITATION',
    // Generic
    'VIEW_MENU',
  ],

  [UserGroup.Employees]: [
    // Health Log
    'CREATE_HEALTH_LOG_MEASUREMENT',
    // Events
    'VIEW_LATEST_EVENTS',
    'CREATE_EVENT',
    // Tasks
    'VIEW_TODAY_TASKS',
    // Work schedule
    'VIEW_WORK_SCHEDULE',
  ],

  [UserGroup.AuthorizedRelatives]: [
    // Relatives
    'VIEW_RELATIVE',
    'VIEW_RELATIVES',
  ],
};
